import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { tokenStore } from '../lib/tokenStore';
import { APP_VERSION } from '../lib/utils';
import { Loader2, Mail, KeyRound, CheckCircle2, Eye, EyeOff } from 'lucide-react';

// Google icon SVG component
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" className="mr-2">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

export const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // OTP flow state
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpMessage, setOtpMessage] = useState('');
  
  // Email verification state
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');

  const { user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Force light mode on login page
  useEffect(() => {
    // Remove dark class when on login page
    document.documentElement.classList.remove('dark');
    
    // Cleanup: Restore user's preference when leaving login page
    return () => {
      const savedTheme = localStorage.getItem('kineserve-theme');
      if (savedTheme === 'dark') {
        document.documentElement.classList.add('dark');
      }
    };
  }, []);
  
  // Registration success state
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  
  // Password validation for registration
  const [passwordErrors, setPasswordErrors] = useState([]);

  const validatePassword = (pwd) => {
    const errors = [];
    if (pwd.length < 12) errors.push('Minimum 12 characters');
    if (!/[A-Z]/.test(pwd)) errors.push('At least 1 uppercase letter');
    if (!/[a-z]/.test(pwd)) errors.push('At least 1 lowercase letter');
    if (!/\d/.test(pwd)) errors.push('At least 1 number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) errors.push('At least 1 special character');
    return errors;
  };

  const handlePasswordChange = (e) => {
    const pwd = e.target.value;
    setPassword(pwd);
    if (!isLogin) {
      setPasswordErrors(validatePassword(pwd));
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.login(email, password, showOtpInput ? otp : null);
      const data = response.data;
      
      // Handle different response scenarios
      if (data.requires_email_verification) {
        setShowVerificationMessage(true);
        setVerificationEmail(email);
        toast.info('Please verify your email to continue');
        return;
      }
      
      if (data.requires_otp && !showOtpInput) {
        setShowOtpInput(true);
        setOtpMessage(data.message);
        toast.info('Verification code sent to your email');
        return;
      }
      
      if (data.access_token) {
        // Successful login - store in secure session storage
        tokenStore.setToken(data.access_token);
        
        // Store password expiry info (non-sensitive, keep in sessionStorage)
        if (data.password_expires_in_days) {
          sessionStorage.setItem('passwordExpiresInDays', data.password_expires_in_days);
        }
        if (data.requires_password_change) {
          sessionStorage.setItem('requiresPasswordChange', 'true');
          toast.warning('Your password has expired. Please change it.');
          navigate('/change-password');
          return;
        }
        
        // Update auth context
        window.location.href = '/dashboard'; // Full reload to update auth context
        toast.success('Logged in successfully');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Authentication failed');
      
      // Reset OTP if invalid
      if (showOtpInput && detail?.includes('OTP')) {
        setOtp('');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validate password
    const errors = validatePassword(password);
    if (errors.length > 0) {
      toast.error('Password does not meet requirements');
      return;
    }
    
    setLoading(true);
    try {
      await api.register({ email, password, full_name: fullName });
      setRegistrationSuccess(true);
      setVerificationEmail(email);
      toast.success('Registration successful! Please check your email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    try {
      await api.resendOtp(email);
      toast.success('New verification code sent');
    } catch (error) {
      toast.error('Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setLoading(true);
    try {
      await api.resendVerification(verificationEmail);
      toast.success('Verification email sent');
    } catch (error) {
      toast.error('Failed to resend verification email');
    } finally {
      setLoading(false);
    }
  };

  // Registration Success Screen
  if (registrationSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4">
        <div className="w-full max-w-md bg-card p-8 rounded-lg shadow-lg text-center">
          <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold mb-2">Check Your Email</h2>
          <p className="text-muted-foreground mb-6">
            We've sent a verification link to <strong>{verificationEmail}</strong>. 
            Please click the link to activate your account.
          </p>
          <div className="space-y-3">
            <Button onClick={handleResendVerification} variant="outline" className="w-full" disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
              Resend Verification Email
            </Button>
            <Button onClick={() => { setRegistrationSuccess(false); setIsLogin(true); }} variant="ghost" className="w-full">
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Email Verification Required Screen
  if (showVerificationMessage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4">
        <div className="w-full max-w-md bg-card p-8 rounded-lg shadow-lg text-center">
          <Mail className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold mb-2">Verify Your Email</h2>
          <p className="text-muted-foreground mb-6">
            Please verify your email address to continue. Check your inbox for the verification link.
          </p>
          <div className="space-y-3">
            <Button onClick={handleResendVerification} variant="outline" className="w-full" disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
              Resend Verification Email
            </Button>
            <Button onClick={() => setShowVerificationMessage(false)} variant="ghost" className="w-full">
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // OTP Entry Screen
  if (showOtpInput) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4">
        <div className="w-full max-w-md bg-card p-8 rounded-lg shadow-lg">
          <div className="text-center mb-6">
            <KeyRound className="h-12 w-12 text-primary mx-auto mb-4" />
            <h2 className="text-2xl font-semibold">Enter Verification Code</h2>
            <p className="text-muted-foreground mt-2">{otpMessage}</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="otp">6-Digit Code</Label>
              <Input
                id="otp"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="text-center text-2xl tracking-widest"
                autoFocus
                required
              />
            </div>
            
            <Button type="submit" className="w-full" disabled={loading || otp.length !== 6}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify & Login'
              )}
            </Button>
            
            <div className="flex gap-2">
              <Button type="button" onClick={handleResendOtp} variant="outline" className="flex-1" disabled={loading}>
                Resend Code
              </Button>
              <Button type="button" onClick={() => { setShowOtpInput(false); setOtp(''); }} variant="ghost" className="flex-1">
                Back
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Main Login/Register Form
  return (
    <div className="min-h-screen flex relative" data-testid="login-page">
      <div className="flex-1 flex items-center justify-center p-8 pb-16 bg-background">
        <div className="w-full max-w-md space-y-8">
          <div className="space-y-2">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="/kineserve-logo.svg" 
                alt="KineServe Logo" 
                className="h-14 w-auto rounded"
                data-testid="kineserve-logo"
              />
              <div>
                <p className="text-lg font-medium text-muted-foreground leading-tight">Service</p>
                <p className="text-lg font-medium text-muted-foreground leading-tight">Management</p>
              </div>
            </div>
            <h2 className="text-3xl font-semibold tracking-tight">
              {isLogin ? 'Welcome back' : 'Create an account'}
            </h2>
            <p className="text-muted-foreground">
              {isLogin ? 'Enter your credentials to access your account' : 'Enter your details to create a new account'}
            </p>
          </div>

          <form onSubmit={isLogin ? handleLogin : handleRegister} className="space-y-4" data-testid="auth-form">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  data-testid="full-name-input"
                  type="text"
                  placeholder="John Doe"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required={!isLogin}
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="password-input"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••••••"
                  value={password}
                  onChange={handlePasswordChange}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Password Requirements for Registration */}
            {!isLogin && password && (
              <div className="bg-muted p-3 rounded-md text-sm">
                <p className="font-medium mb-2 text-xs">Password Requirements:</p>
                <ul className="space-y-1">
                  {['Minimum 12 characters', 'At least 1 uppercase letter', 'At least 1 lowercase letter', 'At least 1 number', 'At least 1 special character'].map((req) => (
                    <li key={req} className={`flex items-center gap-2 text-xs ${!passwordErrors.includes(req) ? 'text-green-600' : 'text-muted-foreground'}`}>
                      <CheckCircle2 className={`h-3 w-3 ${!passwordErrors.includes(req) ? 'text-green-600' : 'text-gray-400'}`} />
                      {req}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading || (!isLogin && passwordErrors.length > 0)}
              data-testid="submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isLogin ? 'Signing in...' : 'Creating account...'}
                </>
              ) : (
                isLogin ? 'Sign in' : 'Create account'
              )}
            </Button>
            
            {/* Forgot Password Link - Only show on login */}
            {isLogin && (
              <div className="text-center">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-muted-foreground hover:text-primary hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot your password?
                </Link>
              </div>
            )}
          </form>

          {/* Google SSO - Only show on login, not registration */}
          {isLogin && (
            <>
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-background text-muted-foreground">Or continue with</span>
                </div>
              </div>
              
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => {
                  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                  const redirectUrl = window.location.origin;
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                data-testid="google-sso-button"
              >
                <GoogleIcon />
                Sign in with Google
              </Button>
              
              <p className="text-xs text-muted-foreground text-center mt-2">
                Google sign-in is only available for existing users
              </p>
            </>
          )}

          <div className="text-center text-sm mt-4">
            <button
              onClick={() => { setIsLogin(!isLogin); setPassword(''); setPasswordErrors([]); }}
              className="text-primary hover:underline"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>
      
      {/* Right side - Branding */}
      <div className="hidden lg:flex lg:flex-1 bg-gradient-to-br from-primary/90 to-primary items-center justify-center p-12">
        <div className="max-w-lg text-white">
          <h1 className="text-4xl font-bold mb-6">KineServe</h1>
          <p className="text-xl text-white/90 mb-8">
            Enterprise-grade IT Service Management platform powered by ITIL best practices.
          </p>
          <ul className="space-y-3 text-white/80">
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5" />
              Incident & Problem Management
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5" />
              SLA Tracking & Compliance
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5" />
              Knowledge Base & Self-Service
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5" />
              AI-Powered Chatbot Support
            </li>
          </ul>
        </div>
      </div>
      
      {/* Copyright Footer with Version */}
      <div className="absolute bottom-0 left-0 right-0 py-3 px-6 bg-transparent">
        <div className="flex justify-between items-center">
          <p className="text-xs text-muted-foreground font-mono" data-testid="login-app-version">
            {APP_VERSION}
          </p>
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Grey Bull Technologies. All rights reserved.
          </p>
          <div className="w-16"></div> {/* Spacer for balance */}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
